<script setup>
import { computed, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import _ from 'lodash'

import IconBase from '@/components/icon/IconBase.vue'
import IconLightning from '@/components/icon/IconLightning.vue'
import { bytes } from '@/filtersNew'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import Badge from '@/components/Badge.vue'
import IconBandwidth from '@/components/icon/IconBandwidth.vue'
import IconInfoSmall from '@/components/icon/IconInfoSmall.vue'

// Store, route, notification
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const router = useRouter()

// Props
const props = defineProps({
	stream: {
		type: Object,
		required: true
	},
	abr: {
		type: Boolean,
	},
	mediaPulse: {
		type: Object,
	},
	compactMode: {
		required: true
	},
	hidePreview: {
		type: Boolean,
		required: false
	},
	transcoderView: {
		type: Boolean,
		default: false
	},
	previewMode: {
		type: Boolean,
		required: false
	},
	storage: {
		type: Boolean,
		required: false
	},
	bandwidth: {
		type: Object,
	}
})

// Computed
const shouldShowAbr = computed(() => props.abr)
const streamAlive = computed(() => props.stream.enabled && props.mediaPulse?.alive)
const wrapperClasses = computed(() => {
	return props.compactMode
		? 'shrink-0'
		: 'absolute !py-2 !px-4 z-20 order-0 w-full'
})

const shouldShowTooltip = computed(() => {
	if (streamAlive.value && props.hidePreview) return true
	if (!props.stream.enabled && props.hidePreview) return true
	return false
})

const tooltipContent = computed(() => {
	if (streamAlive.value && props.hidePreview) return 'This stream is currently live. Adding a live stream may cause issues — add it while it\'s offline.'
	if (!props.stream.enabled) return 'You can\'t stream to a disabled stream, please enable this stream first.'
	return false
})

// Methods

const showInBytes = (value) => {
	return bytes(value)
}
const shouldShowBadwidth = (value) => {
	return value > 104857600 && !props.compactMode
}

</script>

<template>
	<div class="flex gap-x-2 items-center" :class="wrapperClasses">
		<Tooltip wrapper-class="inline-flex z-50" v-if="shouldShowTooltip" :content="tooltipContent">
			<div>
				<icon-base class="text-error">
					<IconInfoSmall />
				</icon-base>
			</div>
		</Tooltip>
		<Badge class="inline-flex items-center gap-1.5" v-if="streamAlive" type="main">
			<span class="relative flex size-1.5">
				<span class="absolute inline-flex h-full w-full rounded-full bg-success-light opacity-75 animate-ping"></span>
				<span class="relative inline-flex rounded-full size-1.5 bg-success-light"></span>
			</span>
			Live<span v-if="!props.hidePreview"> · {{ mediaPulse.bitrate }} kbps</span>
		</Badge>
		<Badge class="inline-flex items-center gap-1.5" v-else-if="props.stream.enabled" type="main">
			<span class="size-1.5 rounded-full bg-surface-6"></span>
			Offline
		</Badge>
		<Badge class="inline-flex items-center gap-1.5" v-else type="main">
			<span class="size-1.5 rounded-full bg-surface-6"></span>
			Disabled
		</Badge>
		<Badge type="main" v-if="shouldShowAbr" class="inline-flex items-center gap-1"><img src="@/assets/icons/abr.svg" alt="ABR">ABR
		</Badge>
		<Badge type="gold" v-if="shouldShowBadwidth(bandwidth)" class="inline-flex ml-auto items-center !mr-4 uppercase">
			<icon-base height="11" width="11">
				<icon-bandwidth />
			</icon-base>
			{{ showInBytes(bandwidth) }}
		</Badge>
		<div v-if="props.stream.type === 'subsecond'"
			class="rounded-full bg-white flex items-center justify-center size-7 ml-auto">
			<icon-base width-view-box="12" height-view-box="16" iconColor="none" width="16" height="16" class="text-dark-7">
				<icon-lightning />
			</icon-base>
		</div>
	</div>
</template>

<style scoped></style>
